// Every heading, marker and label a GitLab issue body is made of — spelled
// ONCE, here, and nowhere else in the repo.
//
// WHY THIS FILE EXISTS. Four writers rebuild an issue description (the refine
// Accept, the manual Save, the split-parent apply, the child composer) and each
// one used to carry its own private list of the sections it knew. The tokens
// themselves were declared in four modules under three vocabularies
// (`SECTION_HEADINGS` in i18n, `BUILD_NOTES_*` / `SUB_ISSUES_HEADING` in
// breakdown, `DESIGN_*` / `WHAT_YOU_NEED_HEADING` in design-section,
// `PLAN_SECTION_HEADING` in apply-update-issue) and spelled inline AGAIN by
// writers and lower-cased again by parsers. That is why "the rebuild deleted a
// section" has been fixed one heading at a time, six times.
//
// PARSE PLANE. Every heading and both marker pairs are English, with ONE
// exception: the three sections a person writes and reads first (`## What you
// need`, `## Business outcome`, `## Acceptance criteria`) also have a German
// spelling (`H_DE`), which a writer uses for a NEW section of a German ticket
// (`headingOf(key, "de")`; owner decision 2026-10-20). The parser
// reads both spellings as the same key, so a body is never split by language.
// The marked blocks (`## Build notes`, `## Design`, `## From mail`,
// `## From meeting`) and every other heading stay English in every locale.
// Only prose INSIDE a block is localized (`BUILD_NOTES_LABELS`,
// `DESIGN_SENTENCES`, @loom/shared/i18n) — those modules import FROM here, never
// the other way round.
//
// This module ships to the browser (the editor parses bodies with it): no
// `node:` API, no regex lookbehind — Safari 14 throws on those at PARSE time,
// where vitest cannot see it.

/** The ten `## ` headings Loom knows, in the spelling it writes. */
export const H = {
  whatYouNeed: "## What you need",
  businessOutcome: "## Business outcome",
  acceptanceCriteria: "## Acceptance criteria",
  definitionOfDone: "## Definition of Done",
  implementationDetails: "## Implementation details",
  subIssues: "## Sub-issues",
  buildNotes: "## Build notes",
  design: "## Design",
  fromMail: "## From mail",
  fromMeeting: "## From meeting",
} as const;

/**
 * The German spellings of the three person-written sections, written for a
 * NEW section of a German ticket and read in any body. `## Definition of
 * Done` is the same in both languages; every other heading has no German
 * spelling.
 */
export const H_DE = {
  whatYouNeed: "## Was es braucht",
  businessOutcome: "## Geschäftlicher Nutzen",
  acceptanceCriteria: "## Akzeptanzkriterien",
} as const;

/** The language a heading is spelled in. */
export type HeadingLang = "en" | "de";

/** Every HTML marker Loom writes into a body. */
export const MARK = {
  buildNotesBegin: "<!-- loom:build-notes -->",
  buildNotesEnd: "<!-- /loom:build-notes -->",
  designBegin: "<!-- loom:design -->",
  designEnd: "<!-- /loom:design -->",
  /** `## From meeting`: the quotes a meeting-filed ticket was
   *  decided on, written once at filing and carried by every later writer. */
  meetingBegin: "<!-- loom:meeting -->",
  meetingEnd: "<!-- /loom:meeting -->",
  /** `## From mail`: the quotes a mail-filed proposal was drafted
   *  from, written once at filing and carried like `## From meeting`. */
  mailBegin: "<!-- loom:mail -->",
  mailEnd: "<!-- /loom:mail -->",
  /** The DoR-reason comment's inner prefix. The marker is rendered AROUND a
   *  payload rather than stored whole, because the payload is what a writer
   *  supplies and what a parsed body carries. */
  dorReasonPrefix: "loom:dor-reason:",
} as const;

/**
 * The child-side backlink LINE, with its trailing ` — <parent title>`.
 *
 * Line-anchored so the title survives a round-trip, and no lookbehind. `.` does
 * not match `\r`, so a CRLF body's carriage return falls outside the match and
 * is never carried into a body written with `\n`.
 */
export const BACKLINK_LINE_RE = /^_Part of #(\d+)_.*$/mu;

/** The bold label `create_issue_with_link_proposals` writes instead of a
 *  heading — the whole of bug B3. Read (and healed) by the parser; never
 *  written by anything. */
export const LEGACY_CRITERIA_LABEL = "**Acceptance criteria**";

export type KnownKey =
  | "what_you_need"
  | "business_outcome"
  | "acceptance_criteria"
  | "definition_of_done"
  | "implementation_details"
  | "sub_issues"
  | "build_notes"
  | "design"
  | "from_mail"
  | "from_meeting";

/**
 * The ONE order every writer renders in.
 *
 * The create form's three sections first, then the refine gate's two, then the
 * five server-written blocks (`## From mail` and `## From meeting` last: each is
 * the record of where the ticket came from, written once at filing, and a body
 * never carries both). Fixed across writers is the point: a body that
 * has been through the editor AND the refine Accept converges on one shape
 * instead of alternating between two.
 */
export const KNOWN_ORDER: readonly KnownKey[] = [
  "what_you_need",
  "business_outcome",
  "acceptance_criteria",
  "definition_of_done",
  "implementation_details",
  "sub_issues",
  "build_notes",
  "design",
  "from_mail",
  "from_meeting",
];

const HEADING_OF: Record<KnownKey, string> = {
  what_you_need: H.whatYouNeed,
  business_outcome: H.businessOutcome,
  acceptance_criteria: H.acceptanceCriteria,
  definition_of_done: H.definitionOfDone,
  implementation_details: H.implementationDetails,
  sub_issues: H.subIssues,
  build_notes: H.buildNotes,
  design: H.design,
  from_mail: H.fromMail,
  from_meeting: H.fromMeeting,
};

const HEADING_OF_DE: Partial<Record<KnownKey, string>> = {
  what_you_need: H_DE.whatYouNeed,
  business_outcome: H_DE.businessOutcome,
  acceptance_criteria: H_DE.acceptanceCriteria,
};

/**
 * The heading line for a key: English, or its German spelling where it has
 * one (`H_DE`; a key without one is English in both).
 */
export const headingOf = (key: KnownKey, lang: HeadingLang = "en"): string =>
  (lang === "de" ? HEADING_OF_DE[key] : undefined) ?? HEADING_OF[key];

/** A heading line's comparison key: the text after `## `, trimmed (which also
 *  drops a CRLF body's `\r`), lower-cased. */
const headingTextOf = (line: string): string =>
  line.slice(3).trim().toLowerCase();

const KEY_BY_TEXT = new Map<string, KnownKey>([
  ...KNOWN_ORDER.map((key) => [headingTextOf(HEADING_OF[key]), key] as const),
  ...(Object.entries(HEADING_OF_DE) as [KnownKey, string][]).map(
    ([key, heading]) => [headingTextOf(heading), key] as const,
  ),
]);

const GERMAN_HEADING_TEXTS = new Set<string>(
  Object.values(HEADING_OF_DE).map((h) => headingTextOf(h)),
);

/**
 * The language a known heading line is spelled in: `de` for a German
 * spelling (`H_DE`), `en` for any other known heading, null for a line that
 * is no known heading. Case-insensitive, as {@link keyOfHeadingLine}.
 */
export const headingLangOfLine = (line: string): HeadingLang | null => {
  if (keyOfHeadingLine(line) === null) return null;
  return GERMAN_HEADING_TEXTS.has(headingTextOf(line)) ? "de" : "en";
};

/**
 * THE ONE BOUNDARY RULE, and the only place it is decided.
 *
 * A line opens a section iff it starts with `## ` — two hashes and ONE SPACE —
 * and is not inside a closed code fence ({@link fencedLineIndices}):
 *
 *  - not `^##\s`: a `##\t` line is CONTENT, so treating it as a boundary would
 *    carry a line the round-trip also keeps — twice;
 *  - `###` is never a boundary (its third character is `#`, not a space), so a
 *    plan's `### Approach` stays inside `## Implementation details` — and a
 *    plan's `### Sub-issues` is no longer mistaken for the real task-list, which
 *    the unanchored `SUB_ISSUES_SECTION_RE` this replaces was.
 *
 * THE FENCE RULE IS A LINE-SET, NOT A PREDICATE, which is why it is a second
 * function: whether a line is inside a fence is a fact about the WHOLE body, and
 * a per-line predicate cannot know it. Callers that split a body into sections
 * compute the set once and skip those indices.
 */
export const isSectionHeadingLine = (line: string): boolean =>
  line.startsWith("## ");

/** A fence delimiter line, with its run in group 1 and its info string in
 *  group 2. No `$` anchor: `.` does not match `\r`, so an anchored form simply
 *  fails on every line of a CRLF body. */
const FENCE_LINE_RE = /^ {0,3}(`{3,}|~{3,})(.*)/u;

/**
 * The line indices inside a CLOSED code fence, delimiters included.
 *
 * WHY THIS EXISTS. A `## ` line inside a fenced block is not a heading — it is
 * an example of one. Treating it as a section boundary is how a ticket whose
 * `## Implementation details` shows an example ticket body lost its real
 * acceptance criteria: the fenced `## Acceptance criteria` parsed as a second,
 * later copy of that heading, `sectionOf` handed the editor the EXAMPLE rows,
 * and the next Save — which owns that heading and therefore replaces every copy
 * of it — wrote the example over the real list and left the plan ending in an
 * unterminated fence. GitLab keeps no description history, so that is
 * unrecoverable. Live #117 and #123 are both in that state.
 *
 * A plan that shows an example ticket body is a shape Loom's own refine writes,
 * so this is the ordinary case, not an exotic one.
 *
 * AN UNCLOSED OPENER IS NOT A FENCE. CommonMark runs an unclosed fence to the
 * end of the document; this does not, deliberately. Honouring it would make
 * every section after a stray ``` invisible to the editor, and a section the
 * editor cannot see is a section the next Save deletes — the same failure, one
 * stray backtick away. An unclosed opener stays an ordinary line, which is also
 * what lets a body an earlier Save already corrupted still be read.
 */
export const fencedLineIndices = (
  lines: readonly string[],
): ReadonlySet<number> => {
  const inside = new Set<number>();
  for (let i = 0; i < lines.length; i += 1) {
    const open = FENCE_LINE_RE.exec(lines[i] ?? "");
    if (open === null) continue;
    const run = open[1] ?? "";
    const char = run[0] ?? "";
    // CommonMark: a backtick fence's info string may not contain a backtick,
    // which is what keeps an inline `` `code` `` span from opening a block.
    if (char === "`" && (open[2] ?? "").includes("`")) continue;
    let close = -1;
    for (let j = i + 1; j < lines.length; j += 1) {
      const end = FENCE_LINE_RE.exec(lines[j] ?? "");
      if (end === null) continue;
      const endRun = end[1] ?? "";
      if ((endRun[0] ?? "") !== char) continue;
      if (endRun.length < run.length) continue;
      // A closer carries no info string.
      if ((end[2] ?? "").trim().length > 0) continue;
      close = j;
      break;
    }
    if (close < 0) continue;
    for (let k = i; k <= close; k += 1) inside.add(k);
    i = close;
  }
  return inside;
};

/** The {@link KnownKey} a heading line names, or null for a heading Loom does
 *  not own. Case-insensitive and CR-tolerant, exactly as the editor compares. */
export const keyOfHeadingLine = (line: string): KnownKey | null => {
  if (!isSectionHeadingLine(line)) return null;
  return KEY_BY_TEXT.get(headingTextOf(line)) ?? null;
};

/** Escape a literal for embedding in a `RegExp` source. No lookbehind, no
 *  named groups — this module ships to the browser. */
const reLiteral = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");

/**
 * The trailing DoR-reason marker, anchored on the marker itself rather than on
 * the newline run in front of it (a leading `\n*` retries at every position of
 * a long run). Capture group 1 is the PAYLOAD.
 */
export const DOR_REASON_TRAILING_RE = new RegExp(
  `<!--\\s*${reLiteral(MARK.dorReasonPrefix)}([^>]*)-->\\s*$`,
  "u",
);

/** A line that is nothing but a DoR-reason marker, wherever it sits. */
export const DOR_REASON_ONLY_LINE_RE = new RegExp(
  `^\\s*<!--\\s*${reLiteral(MARK.dorReasonPrefix)}[^>]*-->\\s*$`,
  "u",
);

/** A line that is nothing but an HTML comment. Machine markers are never field
 *  content, wherever a human edit moved them. */
export const HTML_COMMENT_LINE_RE = /^\s*<!--.*-->\s*$/u;

/** The marker a failing DoR verdict stamps, from its missing-field list. */
export const renderDorReasonMarker = (missing: readonly string[]): string =>
  `<!-- ${MARK.dorReasonPrefix} missing: ${missing.join(", ")} -->`;

/**
 * The field ids inside a DoR-reason payload — {@link renderDorReasonMarker}
 * read backwards. Empty when the payload names none.
 *
 * The payload is machine text (`missing: role, acceptance_criteria`) and was
 * being shown to people as-is: twelve rows of the decisions inbox printed
 * "missing: role", and the issue page's own banner read "DoR fehlt – missing:
 * role" in a German session. A field id is not copy; the browser has the label
 * catalogue and can say "User". A payload a hand edit rewrote parses to nothing
 * rather than to a chip holding a sentence.
 */
export const dorReasonFields = (
  reason: string | null | undefined,
): readonly string[] => {
  if (reason === null || reason === undefined) return [];
  const at = reason.indexOf("missing:");
  if (at < 0) return [];
  // The payload, whether it arrives bare (what `parseIssueBody` hands back) or
  // still wrapped in its marker (what `renderDorReasonMarker` writes).
  const tail = reason.slice(at + "missing:".length);
  const close = tail.indexOf("-->");
  return (close < 0 ? tail : tail.slice(0, close))
    .split(",")
    .map((f) => f.trim())
    .filter((f) => f.length > 0 && /^[a-z0-9_]+$/u.test(f));
};

/** The marker around an arbitrary payload — what the renderer writes, so the
 *  spelling stays in {@link MARK}. */
export const renderDorReasonPayload = (payload: string): string =>
  `<!-- ${MARK.dorReasonPrefix} ${payload} -->`;

/**
 * THE one acceptance-criteria list style: `- row`, one per line.
 *
 * Returns **null** for an empty list, which is the renderer's "mine and empty:
 * write nothing". Both writers go through this. The refine Accept used to keep
 * whatever markers a green already carried, and to leave a single-row value
 * unmarked altogether, while a manual Save always wrote `- ` — so an unchanged
 * Save after an Accept rewrote the body, and the ticket alternated between the
 * two spellings on every write.
 */
export const renderCriteriaRows = (rows: readonly string[]): string | null => {
  const cleaned = rows.map((r) => r.trim()).filter((r) => r.length > 0);
  return cleaned.length === 0 ? null : cleaned.map((r) => `- ${r}`).join("\n");
};

/**
 * A row without its leading list marker — numbered (`1. `), bullet (`- `, `* `,
 * `+ `) or checkbox (`- [ ] `). The editor's three replacements, unchanged and
 * in the editor's order: pre-existing issues use whatever markdown their author
 * typed.
 */
export const stripListMarker = (line: string): string =>
  line
    .replace(/^\s*\d+\.\s*/u, "")
    .replace(/^\s*[-*+]\s*\[[ xX]\]\s*/u, "")
    .replace(/^\s*[-*+]\s*/u, "")
    .trim();

/** A line that opens a markdown list item, in any spelling a human types. Used
 *  only to recognise the legacy criteria block. */
export const LIST_ROW_RE = /^\s*(?:[-*+]|\d+[.)])\s+\S/u;

/** Leading blank LINES dropped (not a first line's own indentation), trailing
 *  whitespace dropped. The one normaliser both the parser and the renderer
 *  apply, so a rendered body re-parses to the values it was rendered from. */
export const trimBlankLines = (text: string): string =>
  text.replace(/^(?:[ \t]*\r?\n)+/u, "").trimEnd();

/**
 * Criterion rows out of a free-text value: one per line, list markers stripped,
 * blanks and marker-only lines dropped.
 *
 * The same reduction the editor applies, so the refine Accept and a manual Save
 * agree on what a criterion's TEXT is. Paired with {@link renderCriteriaRows} it
 * is the round-trip: read a value written in any markdown a human types, write
 * it back in the one style.
 */
export const criteriaRowsFromText = (text: string): string[] =>
  text
    .split("\n")
    .map(stripListMarker)
    .filter((row) => row.length > 0 && !HTML_COMMENT_LINE_RE.test(row));

/**
 * The lead-ins of a meeting epic's `## What you need` lists (its grounded
 * details, then the parts it named), in the meeting's language. One spelling
 * for the drafting and the in-place update, which matches rows under them.
 */
export const EPIC_LIST_LEADS: Readonly<Record<HeadingLang, { readonly details: string; readonly parts: string }>> = {
  de: { details: "Im Meeting dazu gesagt:", parts: "Teile:" },
  en: { details: "Said in the meeting:", parts: "Parts:" },
};

/**
 * The label a drafted value may open with when the meeting only floated it
 * ("Idee: …"). It belongs to an idea alone (reason `idea`): agreed work never
 * carries it, so the drafting and the update strip it (`stripLeadLabel`).
 */
export const LEAD_LABELS: Readonly<Record<HeadingLang, string>> = { de: "Idee", en: "Idea" };

/** `Idee:` / `Idea:` at the start of a value (either language, any case). */
const LEAD_LABEL_RE = /^(idee|idea)\s*:\s*/iu;

/**
 * A value or a `- ` row without its leading `Idee:` / `Idea:` label (a row
 * keeps its marker), and whether one was there. Nothing else changes.
 */
export const stripLeadLabel = (text: string): { readonly text: string; readonly stripped: boolean } => {
  const marker = /^\s*-\s+/u.exec(text)?.[0] ?? "";
  const rest = text.slice(marker.length);
  const label = LEAD_LABEL_RE.exec(rest);
  if (label === null || label[0].length === rest.length) return { text, stripped: false };
  return { text: `${marker}${rest.slice(label[0].length)}`, stripped: true };
};
